import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { duration, titleCase } from '../lib/format';
import type { ApiError } from '../api/client';

interface EnrollmentCard {
  id: string;
  status: 'ACTIVE' | 'COMPLETED' | 'WITHDRAWN' | 'EXPIRED';
  enrolledAt: string;
  completedAt: string | null;
  lastAccessedAt: string | null;
  course: {
    id: string;
    title: string;
    slug: string;
    shortDescription: string;
    difficulty: string;
    durationMinutes: number;
    category: { name: string; slug: string };
    instructors: string[];
  };
  progress: {
    percent: number;
    lessonTotal: number;
    lessonCompleted: number;
    requiredTotal: number;
    requiredCompleted: number;
    timeSpentSeconds: number;
  };
  access: { allowed: boolean; reason?: string };
}

interface MyCoursesResponse {
  items: EnrollmentCard[];
  counts: { all: number; inProgress: number; completed: number; expired: number };
}

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'in-progress', label: 'In progress' },
  { key: 'completed', label: 'Completed' },
  { key: 'expired', label: 'No longer available' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export function MyLearning() {
  // A dashboard tile can link straight to a tab (?filter=completed).
  const [params] = useSearchParams();
  const [tab, setTab] = useState<TabKey>(() => {
    const asked = params.get('filter');
    return TABS.some((entry) => entry.key === asked) ? (asked as TabKey) : 'all';
  });
  const [data, setData] = useState<MyCoursesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (filter: TabKey) => {
    setLoading(true);
    setError(null);
    try {
      setData(await api<MyCoursesResponse>(`/my/courses?filter=${filter}`));
    } catch (caught) {
      setError((caught as ApiError).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(tab);
  }, [load, tab]);

  const counts = data?.counts;

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">My learning</h1>
        <p className="page__lede">Everything you are enrolled in, and how far you have come.</p>
      </div>

      <div className="chips" role="tablist" aria-label="Filter my courses">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            role="tab"
            aria-selected={tab === entry.key}
            className={`pill ${tab === entry.key ? 'pill--active' : 'pill--inactive'}`}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
            {counts ? <span className="muted"> · {countFor(counts, entry.key)}</span> : null}
          </button>
        ))}
      </div>

      {error ? <div className="alert alert--error">{error}</div> : null}

      {loading ? (
        <div className="grid-cards">
          {[0, 1, 2].map((key) => (
            <div className="skel" key={key} style={{ height: 180 }} />
          ))}
        </div>
      ) : data && data.items.length > 0 ? (
        <div className="grid-cards">
          {data.items.map((item) => (
            <EnrollmentTile key={item.id} enrollment={item} />
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="state">
            <div className="state__art" aria-hidden="true">
              ◎
            </div>
            <h4>{emptyTitle(tab)}</h4>
            <p>Browse the catalog and enrol in something.</p>
            <Link className="btn btn--sm" to="/catalog">
              Browse the catalog
            </Link>
          </div>
        </div>
      )}
    </>
  );
}

function EnrollmentTile({ enrollment }: { enrollment: EnrollmentCard }) {
  const { course, progress, access } = enrollment;
  const done = enrollment.status === 'COMPLETED';

  return (
    <article className="lcard">
      <div className="lcard__head">
        <span className="ccard__cat">{course.category.name}</span>
        {done ? (
          <span className="pill pill--approved">Completed</span>
        ) : !access.allowed ? (
          <span className="pill pill--error">No access</span>
        ) : (
          <span className="pill pill--pending">{progress.percent}%</span>
        )}
      </div>

      <h3 className="lcard__title">{course.title}</h3>
      <p className="lcard__desc">{course.shortDescription}</p>

      <div className="bar" aria-hidden="true">
        <i style={{ width: `${progress.percent}%` }} />
      </div>
      <p className="lcard__meta">
        {progress.lessonCompleted} of {progress.lessonTotal} lessons ·{' '}
        {duration(course.durationMinutes)} · {titleCase(course.difficulty)}
      </p>

      {access.allowed ? (
        <Link className="btn btn--sm btn--block" to={`/learn/${course.id}`}>
          {progress.percent === 0 ? 'Start course' : done ? 'Review course' : 'Resume'}
        </Link>
      ) : (
        <>
          <p className="lcard__locked">
            {enrollment.status === 'EXPIRED' || access.reason === 'ENTITLEMENT_EXPIRED'
              ? 'Your access to this course has expired. Your progress is kept.'
              : 'You no longer have access to this course. Your progress is kept.'}
          </p>
          <Link className="btn btn--outline btn--sm btn--block" to={`/courses/${course.slug}`}>
            View course
          </Link>
        </>
      )}
    </article>
  );
}

function countFor(counts: MyCoursesResponse['counts'], key: TabKey): number {
  if (key === 'in-progress') return counts.inProgress;
  if (key === 'completed') return counts.completed;
  if (key === 'expired') return counts.expired;
  return counts.all;
}

function emptyTitle(tab: TabKey): string {
  if (tab === 'completed') return 'Nothing completed yet';
  if (tab === 'in-progress') return 'Nothing in progress';
  if (tab === 'expired') return 'Nothing has expired';
  return 'You are not enrolled in anything yet';
}
